import React, { forwardRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Divider } from '@mui/material';
import ReportPrintHeader from '../../../components/ReportPrintHeader';

const JournalEntryPrint = forwardRef(({ data, entryTypes }, ref) => {
  if (!data) return null;

  // Find Type Label
  const typeLabel = entryTypes?.find(t => t.value === data.entryType)?.label || 'قيد يومية';

  // Calculate Totals
  const totalDebit = data.lines?.reduce((sum, line) => sum + (Number(line.debit) || 0), 0) || 0;
  const totalCredit = data.lines?.reduce((sum, line) => sum + (Number(line.credit) || 0), 0) || 0;

  return (
    <Box ref={ref} sx={{ p: 4, bgcolor: '#fff', direction: 'rtl', minHeight: '100vh' }} className="print-container" dir="rtl">
      {/* Header */}
      <ReportPrintHeader 
        title={typeLabel} 
        subTitle={`رقم القيد: ${data.entryNumber}`}
      />

      {/* Entry Details */}
      <Box sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 2, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">التاريخ:</Typography>
            <Typography variant="body1" fontWeight="bold">
                {data.date ? new Date(data.date).toLocaleDateString('en-GB') : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">الرقم المرجعي:</Typography>
            <Typography variant="body1" fontWeight="bold">{data.referenceNumber || '-'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">البيان:</Typography>
            <Typography variant="body1" fontWeight="bold">{data.description || '-'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Lines Table */}
      <TableContainer sx={{ border: '1px solid #000', mb: 3 }} dir="rtl">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#eee', '& th': { borderBottom: '1px solid #000', fontWeight: 'bold' } }}>
              <TableCell align="center" width="5%">#</TableCell>
              <TableCell align="right" width="35%">الحساب</TableCell>
              <TableCell align="right" width="30%">البيان</TableCell>
              <TableCell align="center" width="15%">مدين</TableCell>
              <TableCell align="center" width="15%">دائن</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.lines?.map((line, index) => (
              <TableRow key={index} sx={{ '& td': { borderBottom: '1px solid #ccc' } }}>
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="right">
                  {/* Account Name logic - assuming line.accountName is passed or accountId is displayed */}
                  {/* Since form uses AccountSelect, we might only have accountId in raw data. 
                      Ideally, the parent should pass enriched data with names. 
                      For now, we display what we have. If AccountSelect passed objects, we use name. */}
                  {line.accountName || line.accountId || '-'}
                </TableCell>
                <TableCell align="right">{line.description}</TableCell>
                <TableCell align="center">{Number(line.debit).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell align="center">{Number(line.credit).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
              </TableRow>
            ))}
            
            {/* Totals Row */}
            <TableRow sx={{ bgcolor: '#f5f5f5', '& td': { fontWeight: 'bold', borderTop: '2px solid #000' } }}>
              <TableCell colSpan={3} align="right">الإجمالي</TableCell>
              <TableCell align="center">{totalDebit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
              <TableCell align="center">{totalCredit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Signatures */}
      <Box sx={{ mt: 8, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">إعداد</Typography>
          <Box sx={{ mt: 4, borderTop: '1px dashed #000', width: 150 }}></Box>
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">مراجعة</Typography>
          <Box sx={{ mt: 4, borderTop: '1px dashed #000', width: 150 }}></Box>
        </Box>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">اعتماد</Typography>
          <Box sx={{ mt: 4, borderTop: '1px dashed #000', width: 150 }}></Box>
        </Box>
      </Box>

      {/* Footer (Powered by) */}
      <div className="report-footer">
        <Typography variant="caption">
            تمت الطباعة بواسطة نظام بروفي إي آر بي | ProfiERP System
        </Typography>
      </div>
      
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </Box>
  );
});

export default JournalEntryPrint;
